import { parse as parseUrl } from 'url';

import { plural } from '@cian/utils';
import { IGetVillageListRequestSchema } from '../../../../node/repositories/countryside-search/entities/schemas/GetVillageListRequestSchema';
import { createVillagesString, formatNumber } from '../..';
import {
  AREA_RANGE_VARIANTS,
  NEW_OBJECTS_KEYS,
  NEW_OBJECTS_NAMES,
  TIndexableObject,
} from '../../../interfaces/villages';
import { INewobjectCategoryForListPresenterSchema } from '../../../../node/repositories/countryside-search/entities/newobject_category/NewobjectCategoryForListPresenterSchema';
import { INewobjectCategorySetForListPresenterSchema } from '../../../../node/repositories/countryside-search/entities/newobject_category/NewobjectCategorySetForListPresenterSchema';
import { IVillageForListPresenterSchema } from '../../../../node/repositories/countryside-search/entities/village/VillageForListPresenterSchema';
import { IReduxThunkContext } from '../../../types/store';
import { seoObjectType } from '../../filters';
import { getSeoUrlPath } from '../../getSeoUrlPath';
import { RealtyRegions } from '../../regions_map';
import { ISeoData } from '../../../types/seo';
import { IRegion } from '../../../types/geo';
import { COMMON_VILLAGES_PATH } from '../../../constants';
import { getMaxMinPrice } from '../../maxMinPrice';

export const getSeoQuery = (url: string): string => {
  const query = parseUrl(url).query;

  if (!query) {
    return '';
  }

  return query.split('&').concat([seoObjectType]).sort().join('&');
};

export async function createRegionSeoUrl(
  context: IReduxThunkContext,
  regions: IRegion[],
  filters: IGetVillageListRequestSchema,
  oldRegionIdRaw: number | null | undefined,
) {
  let regionId = filters.regionId;
  let newFilters = filters;
  let oldRegionId = oldRegionIdRaw;

  if (!regionId || regionId === RealtyRegions.Moscow) {
    regionId = RealtyRegions.MoscowArea; // Если регион Москва или его нет - берем Московскую обл.
  } else if (regionId === RealtyRegions.StPetersburg) {
    regionId = RealtyRegions.StPetersburgArea; // Если регион Санкт-Петербург - берем Ленинградскую обл.
  }

  if (oldRegionId === RealtyRegions.Moscow) {
    oldRegionId = RealtyRegions.MoscowArea;
  } else if (oldRegionId === RealtyRegions.StPetersburg) {
    oldRegionId = RealtyRegions.StPetersburgArea;
  }

  if (oldRegionId !== regionId) {
    newFilters = {
      ...newFilters,
      road: [],
      roadDistance: undefined,
      station: [],
      stationDistance: undefined,
    };
  }

  const newRegion = regions.find(region => region.id === regionId);

  const host = newRegion && newRegion.baseHost ? newRegion.baseHost : getBaseHost();

  const path = await getSeoUrlPath(context, COMMON_VILLAGES_PATH, newFilters);

  return `${host}${path}`;
}

// Базовый хост с протоколом и сабдоменом www
export function getBaseHost() {
  return window && `${window.location.protocol}//${window.location.host.replace(/(.*:\/\/)([^.]*)\.(.*)/, '$1www.$3')}`;
}

export const SEO_TEMPLATES: TIndexableObject = {
  minArea: '[мин.площадь]',
  minPrice: '[мин.цена]',
  totalItems: '[найдено объявлений]',
};

export const removeWrongTags = (field: string) =>
  field.replace(/(\[(?!(мин\.площадь|мин\.цена|найдено объявлений)).*])/gi, '');

export const seoTemplatesFormatter = (field: string, options: TIndexableObject) => {
  let result = removeWrongTags(field);
  Object.keys(options).forEach((key: string) => {
    if (key in SEO_TEMPLATES) {
      result = result.replace(SEO_TEMPLATES[key], options[key]);
    }
  });

  return result;
};

export const titleTemplateFormatter = (field: string, options: TIndexableObject) => {
  /**
   * Если в шаблоне для `title` не найден плейсхолдер счетчика объявлений,
   * добавляем его в конец. Это требования SEO, он нужен всегда.
   */
  const titleField = /\[найдено объявлений\]/gi.test(field) ? field : field + ' [найдено объявлений]';

  return seoTemplatesFormatter(titleField, options);
};

export const hasOnlyLandplots = (villageList: IVillageForListPresenterSchema[]): boolean =>
  villageList.every(
    ({ newobjectCategorySet }) =>
      !!newobjectCategorySet &&
      !newobjectCategorySet[NEW_OBJECTS_KEYS.COTTAGE] &&
      !newobjectCategorySet[NEW_OBJECTS_KEYS.TOWNHOUSE] &&
      !newobjectCategorySet[NEW_OBJECTS_KEYS.LOWRISEHOUSE],
  );

const DEFAULT_AREA_MIN: IWithAreaMin = { areaMin: +Infinity };

export const reduceMinArea = (acc: IWithAreaMin, { areaMin }: INewobjectCategoryForListPresenterSchema) => {
  const minArea = areaMin || 0;

  return {
    areaMin: minArea < acc.areaMin ? minArea : acc.areaMin,
  };
};

export const getObjectsMinArea = (village: IVillageForListPresenterSchema) =>
  Object.keys(NEW_OBJECTS_NAMES).reduce((acc, key) => {
    if (!village.newobjectCategorySet) {
      return acc;
    }
    const item = Object.assign(
      {},
      village.newobjectCategorySet[key as keyof INewobjectCategorySetForListPresenterSchema],
    );
    if (!item.areaMin) {
      return acc;
    }
    if (key === NEW_OBJECTS_KEYS.LANDPLOT) {
      item.areaMin *= 100;
    }

    return reduceMinArea(acc, item);
  }, DEFAULT_AREA_MIN);

export const getSeoMinArea = (villageList: IVillageForListPresenterSchema[]): string => {
  let { areaMin } = villageList.map(getObjectsMinArea).reduce(reduceMinArea, DEFAULT_AREA_MIN);
  if (!areaMin) {
    return '';
  }
  const villageListHasOnlyLandplots = hasOnlyLandplots(villageList);
  const postfix = villageListHasOnlyLandplots ? plural(areaMin, AREA_RANGE_VARIANTS) : 'м²';
  if (villageListHasOnlyLandplots) {
    areaMin /= 100;
  }

  return `${formatNumber(areaMin)} ${postfix}`;
};

export const getFormattedSeoData = (
  seoData: ISeoData | undefined | null,
  villageList: IVillageForListPresenterSchema[] | undefined,
  totalItems: number | null | undefined,
) => {
  if (!seoData || !villageList) {
    return undefined;
  }
  const options = {
    minArea: getSeoMinArea(villageList),
    minPrice: String(getMaxMinPrice(villageList).priceMin),
    totalItems: createVillagesString(totalItems || 0),
  };
  const result: ISeoData = Object.assign({}, seoData);
  Object.keys(result).forEach((key: keyof typeof result) => {
    const field = result[key];
    if (field && typeof field === 'string') {
      (result[key] as string) =
        key === 'title' ? titleTemplateFormatter(field, options) : seoTemplatesFormatter(field, options);
    }
  });

  return result;
};

interface IWithAreaMin {
  areaMin: number;
}

export const isSeoFieldInvalid = (field: string) =>
  /(\[(?!(мин\.площадь|мин\.цена|найдено объявлений)).*])/gi.test(field);

export const checkInvalidSeoFields = (seoData: ISeoData | null | undefined) => {
  if (!seoData) {
    return false;
  }

  return Object.values(seoData).some(isSeoFieldInvalid);
};
